import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CARD_BACKS, TABLE_FELTS, type UserSettings } from '@cardhub/protocol';
import { CardBack } from '../cards/PlayingCard';
import { api } from '../api/client';
import { Avatar, Icon, Modal, Segmented, Toggle, TopBar } from '../components/ui';
import { useT, type MessageKey } from '../i18n';
import { errorMessage } from '../lib/hooks';
import { saveSettings, useSession } from '../store/session';
import { useSettings } from '../store/settings';
import { useCosmetics } from './Shop';
import { toast } from '../store/ui';

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const FELT_PREVIEW: Record<string, React.CSSProperties> = {
  emerald: { background: 'radial-gradient(#0f6b4f, #0a4636)', ['--rim' as string]: '#3b2a17' },
  royal: { background: 'radial-gradient(#1f4aa8, #0f2257)', ['--rim' as string]: '#2b1d0e' },
  maroon: { background: 'radial-gradient(#8f1d2c, #4a0a14)', ['--rim' as string]: '#2a1a0c' },
  charcoal: { background: 'radial-gradient(#3f4654, #151a23)', ['--rim' as string]: '#57401f' },
  teal: { background: 'radial-gradient(#138a80, #064e4b)', ['--rim' as string]: '#3b2a17' },
};

const AVATARS = Array.from({ length: 24 }, (_, i) => `avatar_${String(i + 1).padStart(2, '0')}`);

function MfaSection({ enabled, onChange }: { enabled: boolean; onChange: () => void }) {
  const t = useT();
  const [setup, setSetup] = useState<{ secret: string; qrSvg: string } | null>(null);
  const [code, setCode] = useState('');
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn();
      toast(ok, 'success');
      setSetup(null);
      setCode('');
      onChange();
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };
  return (
    <div className="card col">
      <div className="spread">
        <span>
          <strong>{t('mfa.title')}</strong>
          <br />
          <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{enabled ? t('mfa.onBody') : t('mfa.offBody')}</span>
        </span>
        <span className={`pill ${enabled ? 'accent' : ''}`}>{enabled ? 'ON' : 'OFF'}</span>
      </div>
      {!enabled && !setup && (
        <button className="btn" onClick={async () => { try { setSetup(await api.post<{ secret: string; qrSvg: string }>('/api/v1/auth/mfa/setup')); } catch (e) { toast(errorMessage(e), 'error'); } }}>
          {t('mfa.setup')}
        </button>
      )}
      {setup && (
        <>
          <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{t('mfa.scan')}</span>
          <div className="center" style={{ background: '#fff', borderRadius: 12, padding: 8, width: 236, margin: '0 auto' }} dangerouslySetInnerHTML={{ __html: setup.qrSvg }} aria-label={t('mfa.qr')} />
          <code className="num" style={{ textAlign: 'center', wordBreak: 'break-all' }}>{setup.secret}</code>
        </>
      )}
      {(setup || enabled) && (
        <div className="row">
          <input className="input num" inputMode="numeric" maxLength={6} placeholder="123456" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} aria-label={t('mfa.code')} />
          {setup ? (
            <button className="btn primary" disabled={code.length !== 6} onClick={() => void run(() => api.post('/api/v1/auth/mfa/enable', { code }), t('mfa.enabled'))}>{t('mfa.enable')}</button>
          ) : (
            <button className="btn danger" disabled={code.length !== 6} onClick={() => void run(() => api.post('/api/v1/auth/mfa/disable', { code }), t('mfa.disabled'))}>{t('mfa.disable')}</button>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="list-item spread">
      <span>{label}</span>
      {children}
    </div>
  );
}

export function Settings() {
  const t = useT();
  const nav = useNavigate();
  const s = useSettings((x) => x.settings);
  const user = useSession((x) => x.user)!;
  const logout = useSession((x) => x.logout);
  const refreshMe = useSession((x) => x.refreshMe);
  const [del, setDel] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [avatarOpen, setAvatarOpen] = useState(false);
  const set = (patch: Partial<UserSettings>) => void saveSettings(patch);
  const cosmetics = useCosmetics();

  const exportData = async () => {
    try {
      const data = await api.get('/api/v1/me/export');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'cardhub-data.json';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast(errorMessage(e), 'error');
    }
  };

  return (
    <main className="page">
      <TopBar title={t('settings.title')} onBack={() => nav(-1)} />

      <div className="list">
        <button className="list-item" onClick={() => setAvatarOpen(true)}>
          <Avatar avatar={user.avatar} size={44} />
          <span className="grow"><strong>{user.username}</strong><br /><span className="muted">Change avatar</span></span>
          <Icon name="back" className="" />
        </button>
      </div>

      <h2 className="section-title">{t('settings.language')}</h2>
      <Segmented label={t('settings.language')} value={s.language} onChange={(v) => set({ language: v })} options={[{ value: 'en', label: 'English' }, { value: 'ne', label: 'नेपाली' }, { value: 'hi', label: 'हिन्दी' }]} />

      <h2 className="section-title">{t('settings.theme')}</h2>
      <Segmented label={t('settings.theme')} value={s.theme} onChange={(v) => set({ theme: v })} options={[{ value: 'dark', label: t('settings.theme.dark') }, { value: 'light', label: t('settings.theme.light') }, { value: 'system', label: t('settings.theme.system') }]} />

      <h2 className="section-title">{t('cosmetics.title')}</h2>
      <div className="card col">
        <Link to="/shop" className="btn gold">Get more in the Shop</Link>
        <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{t('cosmetics.cardBack')}</span>
        <div className="cosmetic-grid">
          {CARD_BACKS.map((c) => {
            const locked = !(cosmetics.owned?.cardBacks.includes(c.id) ?? c.price === 0);
            return (
              <button key={c.id} className="cosmetic" aria-pressed={s.cardBack === c.id} disabled={locked} onClick={() => set({ cardBack: c.id })} aria-label={`${t(`cosmetics.back.${c.id}` as MessageKey)}${locked ? ` — locked` : ''}`}>
                <span className="card-back-preview"><CardBack variant={c.id} /></span>
                <span>{locked ? `🔒 ${usd(c.price)}` : t(`cosmetics.back.${c.id}` as MessageKey)}</span>
              </button>
            );
          })}
        </div>
        <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>{t('cosmetics.felt')}</span>
        <div className="cosmetic-grid">
          {TABLE_FELTS.map((c) => {
            const locked = !(cosmetics.owned?.tableFelts.includes(c.id) ?? c.price === 0);
            return (
              <button key={c.id} className="cosmetic" aria-pressed={s.tableFelt === c.id} disabled={locked} onClick={() => set({ tableFelt: c.id })} aria-label={`${t(`cosmetics.felt.${c.id}` as MessageKey)}${locked ? ` — locked` : ''}`}>
                <span className="felt-preview" style={FELT_PREVIEW[c.id]} />
                <span>{locked ? `🔒 ${usd(c.price)}` : t(`cosmetics.felt.${c.id}` as MessageKey)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <h2 className="section-title">Audio & feel</h2>
      <div className="list">
        <Row label={t('settings.sound')}><Toggle checked={s.sound} onChange={(v) => set({ sound: v })} label={t('settings.sound')} /></Row>
        <Row label={t('settings.music')}><Toggle checked={s.music} onChange={(v) => set({ music: v })} label={t('settings.music')} /></Row>
        <div className="list-item col" style={{ alignItems: 'stretch' }}>
          <label htmlFor="vol">{t('settings.volume')}</label>
          <input id="vol" type="range" min={0} max={1} step={0.05} value={s.masterVolume} onChange={(e) => set({ masterVolume: Number(e.target.value) })} />
        </div>
        <Row label={t('settings.haptics')}><Toggle checked={s.haptics} onChange={(v) => set({ haptics: v })} label={t('settings.haptics')} /></Row>
      </div>

      <h2 className="section-title">Accessibility & graphics</h2>
      <div className="list">
        <div className="list-item spread">
          <span>{t('settings.graphics')}</span>
          <Segmented label={t('settings.graphics')} value={s.graphics} onChange={(v) => set({ graphics: v })} options={[{ value: 'low', label: 'Low' }, { value: 'medium', label: 'Med' }, { value: 'high', label: 'High' }]} />
        </div>
        <Row label={t('settings.reducedMotion')}><Toggle checked={s.reducedMotion} onChange={(v) => set({ reducedMotion: v })} label={t('settings.reducedMotion')} /></Row>
        <Row label={t('settings.highContrast')}><Toggle checked={s.highContrast} onChange={(v) => set({ highContrast: v })} label={t('settings.highContrast')} /></Row>
        <Row label={t('settings.colorBlind')}><Toggle checked={s.colorBlindCards} onChange={(v) => set({ colorBlindCards: v })} label={t('settings.colorBlind')} /></Row>
        <div className="list-item col" style={{ alignItems: 'stretch' }}>
          <label htmlFor="fsz">{t('settings.fontSize')} ({Math.round(s.fontScale * 100)}%)</label>
          <input id="fsz" type="range" min={0.85} max={1.4} step={0.05} value={s.fontScale} onChange={(e) => set({ fontScale: Number(e.target.value) })} />
        </div>
      </div>

      <h2 className="section-title">{t('settings.notifications')}</h2>
      <div className="list">
        {(Object.keys(s.notifications) as (keyof UserSettings['notifications'])[]).map((k) => (
          <Row key={k} label={t(`notif.${k}` as MessageKey)}>
            <Toggle checked={s.notifications[k]} onChange={(v) => set({ notifications: { ...s.notifications, [k]: v } })} label={t(`notif.${k}` as MessageKey)} />
          </Row>
        ))}
      </div>

      <h2 className="section-title">{t('settings.privacy')}</h2>
      <div className="list">
        <Row label={t('settings.showOnline')}><Toggle checked={s.privacy.showOnlineStatus} onChange={(v) => set({ privacy: { ...s.privacy, showOnlineStatus: v } })} label={t('settings.showOnline')} /></Row>
        <Row label={t('settings.allowRequests')}><Toggle checked={s.privacy.allowFriendRequests} onChange={(v) => set({ privacy: { ...s.privacy, allowFriendRequests: v } })} label={t('settings.allowRequests')} /></Row>
        <Row label={t('settings.leaderboards')}><Toggle checked={s.privacy.showInLeaderboards} onChange={(v) => set({ privacy: { ...s.privacy, showInLeaderboards: v } })} label={t('settings.leaderboards')} /></Row>
        <Row label={t('settings.analytics')}><Toggle checked={s.privacy.analyticsConsent} onChange={(v) => set({ privacy: { ...s.privacy, analyticsConsent: v } })} label={t('settings.analytics')} /></Row>
      </div>

      <h2 className="section-title">{t('settings.security')}</h2>
      <MfaSection enabled={user.mfaEnabled} onChange={() => void refreshMe()} />

      <h2 className="section-title">{t('settings.account')}</h2>
      <div className="list">
        <Link to="/help" className="list-item" style={{ color: 'inherit' }}><Icon name="info" /> <span className="grow">{t('settings.help')}</span></Link>
        <button className="list-item" onClick={() => void exportData()}><Icon name="share" /> <span className="grow">{t('settings.export')}</span></button>
        {['MODERATOR', 'SUPPORT', 'ADMIN', 'SUPER_ADMIN'].includes(user.role) && (
          <Link to="/admin" className="list-item" style={{ color: 'inherit' }}><Icon name="shield" /> <span className="grow">Admin dashboard</span></Link>
        )}
        <button className="list-item" onClick={() => void logout().then(() => nav('/welcome'))}><Icon name="logout" /> <span className="grow">{t('auth.logout')}</span></button>
        <button className="list-item" style={{ color: 'var(--danger)' }} onClick={() => setDel(true)}><Icon name="close" /> <span className="grow">{t('settings.delete')}</span></button>
      </div>
      <p className="muted" style={{ fontSize: 'var(--fs-xs)', textAlign: 'center', marginTop: 24 }}>{t('settings.legal')} · v0.1.0</p>

      <Modal open={del} onClose={() => setDel(false)} title={t('settings.delete')}>
        <p className="dim">{t('settings.deleteConfirm')}</p>
        <input className="input" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-label="Type DELETE" />
        <button
          className="btn danger block"
          style={{ marginTop: 12 }}
          disabled={confirm !== 'DELETE'}
          onClick={async () => {
            try {
              await api.del('/api/v1/me', { confirm: 'DELETE' });
              await logout();
              nav('/welcome');
            } catch (e) {
              toast(errorMessage(e), 'error');
            }
          }}
        >
          {t('settings.delete')}
        </button>
      </Modal>
      <Modal open={avatarOpen} onClose={() => setAvatarOpen(false)} title="Avatar">
        <div className="avatar-grid">
          {AVATARS.map((a) => (
            <button
              key={a}
              aria-pressed={a === user.avatar}
              aria-label={a}
              onClick={async () => {
                try {
                  await api.patch('/api/v1/me', { avatar: a });
                  await refreshMe();
                  setAvatarOpen(false);
                } catch (e) {
                  toast(errorMessage(e), 'error');
                }
              }}
            >
              <Avatar avatar={a} size={52} />
            </button>
          ))}
        </div>
      </Modal>
    </main>
  );
}
